import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/store";
import { useEffect } from "react";
import { setInitialMailFlow } from "../store/score/scoreSlice";
import { updateMiqCollection } from "wasp/client/operations";
import { mailer } from "../services/mail/mailer";
import { generateEmailContent } from './emailContentGenerator';


const extractShopName = (url: string): string => {
  const pattern = /^(?:https?:\/\/)?(?:www\.)?([^.]+)(?:\.myshopify\.com|\.com|\.net|\.org)/i;
  const match = url.match(pattern);
  const name = match ? match[1] : "Your Store";
  return name.charAt(0).toUpperCase() + name.slice(1);
};

const useSendInitialScoreMail = () => {
  const { initialMailFlow, initialMailScores } = useSelector(
    (state: RootState) => state.score
  );
  const { currentCollection, currentUser } = useSelector(
    (state: RootState) => state.user
  );
  const dispatch = useDispatch();

  useEffect(() => {
    const sendEmail = async () => {

      const shopName = currentCollection?.collection?.miqShopifyDomain 
      ? extractShopName(currentCollection?.collection?.miqShopifyDomain )
      : "Your Store";

      // Map available scores to required scores
      const emailContent = generateEmailContent(
        shopName,
        {
          conversion: initialMailScores?.conversionScore ?? 0,
          discovery: initialMailScores?.discoveryScore ?? 0,
          search: initialMailScores?.searchScore ?? 0,
          average: initialMailScores?.overallScore ?? 0,
          // Map main scores to quality scores since we don't have the granular ones
          titleQuality: initialMailScores?.titleScore ?? 0,
          descriptionQuality: initialMailScores?.descriptionScore ?? 0,
          imageQuality: initialMailScores?.imageScore ?? 0,
          seoQuality: initialMailScores?.seoScore ?? 0,
          altTagsQuality: initialMailScores?.altTagsScore ?? 0,
          categoryQuality: initialMailScores?.categoryScore ?? 0
        }
      );

      await mailer.sendScoreEmail({
        email_to: currentUser?.email!,
        OVERALL_SCORE: initialMailScores?.overallScore ?? 0,
        COMPETITOR_AVG: initialMailScores?.competitorAvg ?? 0,
        CONVERSION_SCORE: initialMailScores?.conversionScore ?? 0,
        DISCOVERY_SCORE: initialMailScores?.discoveryScore ?? 0,
        SEARCH_SCORE: initialMailScores?.searchScore ?? 0,
        // Dynamic content from our generator
        SUBJECT_LINE: emailContent.subjectLine,
        QUICK_WINS_INTRO: emailContent.quickWinsIntro,
        QUICK_WINS: emailContent.quickWins.map(win => `• ${win}`).join('\n'),
        OVERALL_MESSAGE: emailContent.overallMessage,
        CONVERSION_MESSAGE: emailContent.metrics.conversion,
        DISCOVERY_MESSAGE: emailContent.metrics.discovery,
        SEARCH_MESSAGE: emailContent.metrics.search,
      });
    };

    if (currentCollection.collection) {
      if (initialMailFlow === null) {
        if (currentCollection.collection.initialContentScoreJSON) {
          dispatch(setInitialMailFlow({ sent: true }));
        } else {
          dispatch(setInitialMailFlow({ sent: false }));
        }
      } else {
        if (!initialMailFlow.sent && initialMailScores !== null) {
          sendEmail();
          dispatch(setInitialMailFlow({ sent: true }));
          updateMiqCollection({
            id: currentCollection.collection.id,
            initialContentScoreJSON: JSON.stringify({
              overallScore: initialMailScores?.overallScore,
              competitorAvg: initialMailScores?.competitorAvg,
              conversionScore: initialMailScores?.conversionScore,
              discoveryScore: initialMailScores?.discoveryScore,
              searchScore: initialMailScores?.searchScore,
            }),
          });
        }
      }
    }
  }, [initialMailFlow, currentCollection.collection, initialMailScores]);
};

export default useSendInitialScoreMail;